import {QueryErrorResetBoundary, useQuery} from '@tanstack/react-query'
import * as apps from 'constants/apps'
import {compact} from 'lodash'
import type {SnackbarKey} from 'notistack'
import {useSnackbar} from 'notistack'
import {Suspense, useEffect, useMemo} from 'react'
import {createRoutesFromElements, Navigate, Route} from 'react-router'
import {Outlet, RouterProvider, useRouteError} from 'react-router-dom'
import config from '../config'
import AppShell from './components/containers/AppShell'
import ErrorFallback from './components/screens/ErrorFallback'
import SplashScreen from './components/screens/SplashScreen'
import Login from './components/screens/login/Login'
import {ApiProvider, useOnline} from './hooks/api'
import {AuthProvider, useSession} from './hooks/auth'
import {AlertsProvider} from './hooks/useAlert'
import {MenuProvider, useMenu} from './hooks/useMenu'
import Sentry, {createSentryBrowserRouter} from './sentry'
import ThemeProvider from './theme/ThemeProvider'


const Shell = () => {
  const {openMenu, setOpenMenu} = useMenu()
  const menuClick = () => {
    setOpenMenu((prevOpen) => !prevOpen)
  }

  return (
    <AppShell onMenuClick={menuClick} openMenu={openMenu}>
      <Suspense fallback={<SplashScreen />}>
        <Outlet />
      </Suspense>
    </AppShell>
  )
}

const ErrorElement = () => {
  const error = useRouteError()
  useEffect(() => {
    if (config.sentry) {
      Sentry.captureException(error)
    }
  }, [error])
  throw error
}


const Screens = () => {
  const session = useSession()
  const isOnline = useOnline()
  const {enqueueSnackbar, closeSnackbar} = useSnackbar()
  const {data: routes} = useQuery({
    queryKey: ['getRoutes', session],
    queryFn: async () => {
      if (!session) return null
      const routeImports = await Promise.all([
        import('./components/screens'),
        session.apps?.includes(apps.OLUP) ? import('./apps/olup/screens') : null,
        session.apps?.includes(apps.UTZ) ? import('./apps/utz/screens') : null,
      ])
      return compact(routeImports.map((routeImport) => routeImport?.default))
    },
    suspense: true,
    keepPreviousData: false,
  })

  useEffect(() => {
    let snackbarKey: SnackbarKey | null = null
    if (!isOnline) {
      snackbarKey = enqueueSnackbar('Nemáte připojení k internetu nebo server je nedostupný', {
        variant: 'error',
        persist: true,
      })
    }
    return () => {
      if (snackbarKey) {
        closeSnackbar(snackbarKey)
        snackbarKey = null
      }
    }
  }, [isOnline, enqueueSnackbar, closeSnackbar])


  const router = useMemo(() => {
    if (session) {
      return createSentryBrowserRouter([
        {
          id: 'root',
          element: <Shell />,
          errorElement: <ErrorElement />,
          children: createRoutesFromElements(
            <>
              <Route path="/login" element={<Navigate to="/" />} />
              {routes?.map((getRoutes) => getRoutes())}
            </>,
          ),
        },
      ])
    }

    return createSentryBrowserRouter([
      {
        id: 'root',
        errorElement: <ErrorElement />,
        children: createRoutesFromElements(
          <>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </>,
        ),
      },
    ])
    // We don't use session?.token inside useMemo, but we still want
    // to rerender the router when the token changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.token])

  return (
    <RouterProvider router={router} />
  )
}


const App = () => (
  <QueryErrorResetBoundary>
    {({reset}) => (
      <Sentry.ErrorBoundary
          onReset={reset}
          fallback={({resetError}) => <ErrorFallback resetError={resetError} />}
      >
        <ThemeProvider>
          <AlertsProvider>
            <AuthProvider>
              <ApiProvider>
                <MenuProvider>
                  <Screens />
                </MenuProvider>
              </ApiProvider>
            </AuthProvider>
          </AlertsProvider>
        </ThemeProvider>
      </Sentry.ErrorBoundary>
    )}
  </QueryErrorResetBoundary>
)


export default App
